import { baseApi } from "./baseApi";
import * as API from "@utils/apiPath";
import type {
  ContactEnquiryApiResponse,
  ContactEnquiryItem,
  ContactEnquiryTableFilterParams,
  ContactEnquiryTicketCounts,
  ContactEnquiryTicketStatus,
} from "interface/contactEnquiry";
import type { Pagination } from "interface/common";
import { PAGE_LIMIT } from "@utils/constant/common";

function coercePagination(raw: unknown): Pagination {
  const p = raw as Record<string, unknown> | null | undefined;
  return {
    total: Number(p?.total ?? 0),
    page: Number(p?.page ?? 1),
    currentPage: Number(p?.currentPage ?? p?.page ?? 1),
    limit: Number(p?.limit ?? 10),
  };
}

function coerceCounts(raw: unknown): ContactEnquiryTicketCounts {
  const c = raw as Record<string, unknown> | null | undefined;
  return {
    all: Number(c?.all ?? 0),
    pending: Number(c?.pending ?? 0),
    completed: Number(c?.completed ?? 0),
  };
}

function normalizeTicketStatus(raw: unknown): ContactEnquiryTicketStatus {
  const s = String(raw ?? "pending");
  if (s === "in_progress" || s === "completed") return s;
  return "pending";
}

function mapRaw(raw: Record<string, unknown>): ContactEnquiryItem {
  return {
    id: String(raw.id ?? raw._id ?? ""),
    enquiryType: String(raw.enquiryType ?? ""),
    firstName: String(raw.firstName ?? ""),
    lastName: String(raw.lastName ?? ""),
    phone: String(raw.phone ?? ""),
    email: String(raw.email ?? ""),
    details: String(raw.details ?? ""),
    ticketStatus: normalizeTicketStatus(raw.ticketStatus),
    createdAt: raw.createdAt != null ? String(raw.createdAt) : undefined,
    updatedAt: raw.updatedAt != null ? String(raw.updatedAt) : undefined,
  };
}

function normalizeListResponse(payload: unknown): ContactEnquiryApiResponse {
  const body = payload as {
    statusCode?: number;
    message?: string;
    data?: {
      results?: Record<string, unknown>[];
      pagination?: unknown;
      counts?: unknown;
    };
  };
  const results = (body.data?.results ?? []).map((r) => mapRaw(r));
  return {
    statusCode: Number(body.statusCode ?? 200),
    message: String(body.message ?? ""),
    data: {
      results,
      pagination: coercePagination(body.data?.pagination),
      counts: coerceCounts(body.data?.counts),
    },
  };
}

function getFormattedParams(
  params: ContactEnquiryTableFilterParams,
): Record<string, string | number> {
  const p = params ?? {};
  const result: Record<string, string | number> = {
    page: p.page ?? 1,
    limit: p.pageSize ?? PAGE_LIMIT,
  };

  const email = Array.isArray(p.email) ? p.email[0] : p.email;
  if (email && String(email).trim()) {
    result.q = String(email).trim();
  }

  const types = p.enquiryType;
  if (Array.isArray(types) && types.length) {
    result.enquiryType = types.map(String).join("|");
  }

  const createdAt = Array.isArray(p.createdAt) ? p.createdAt[0] : p.createdAt;
  if (createdAt && String(createdAt).trim()) {
    result.createdAt = String(createdAt).trim();
  }

  const ticketStatus = p.ticketStatus;
  if (Array.isArray(ticketStatus) && ticketStatus.length) {
    result.ticketStatus = ticketStatus.map(String).join("|");
  }

  if (p.sortField) {
    result.sort = String(p.sortField);
  }
  if (p.sortOrder === "ascend") {
    result.direction = "asc";
  } else if (p.sortOrder === "descend") {
    result.direction = "desc";
  }

  return result;
}

export const contactEnquiryApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    getContactEnquiries: builder.query<
      ContactEnquiryApiResponse,
      ContactEnquiryTableFilterParams
    >({
      query: (params) => ({
        url: API.GET_CONTACT_ENQUIRY,
        method: "GET",
        params: getFormattedParams(params),
      }),
      transformResponse: normalizeListResponse,
      providesTags: ["ContactEnquiry"],
    }),
    updateContactEnquiryStatus: builder.mutation<
      unknown,
      { id: string; ticketStatus: ContactEnquiryTicketStatus }
    >({
      query: (body) => ({
        url: `${API.GET_CONTACT_ENQUIRY}/status`,
        method: "PUT",
        body,
      }),
      invalidatesTags: ["ContactEnquiry"],
    }),
  }),
});

export const { useGetContactEnquiriesQuery, useUpdateContactEnquiryStatusMutation } =
  contactEnquiryApi;
